import {
  Feedback,
  JournalDetailsFeedback,
  PaperDetails,
  PaperDetailsFeedback,
  StudyDetailsResponse,
  StudySnapshotFeedback,
} from "helpers/api";
import { camelCaseToLabel } from "helpers/format";
import { formatAuthors } from "helpers/paper";
import {
  parseStudyDetailsResponse,
  StudySnapshotFields,
} from "helpers/studyDetail";
import { studyTypeBadges } from "helpers/studyTypes";
import { ReactNode } from "react";

export interface FeedbackInputType extends Feedback {
  pristine: boolean;
}

export interface FeedbackItemContent {
  name: string;
  content: ReactNode;
  label: string;
  original: string | number;
}

export type StateType = {
  [key: string]: FeedbackInputType;
};

type FeedbackStateKeyType =
  | keyof PaperDetailsFeedback
  | keyof StudySnapshotFeedback
  | keyof JournalDetailsFeedback;
type FeedbackStateData = {
  paperDetails?: PaperDetailsFeedback;
  studySnapshot?: StudySnapshotFeedback;
  journalDetails?: JournalDetailsFeedback;
};

export const UNKNOWN_QUARTILE_VALUE = "UNKNOWN";

export const generateFeedbackItemPayload = (
  category: string,
  name: string,
  content: ReactNode,
  label: string,
  original?: string | number
): FeedbackItemContent => {
  const key = category ? category + ":" + name : name;
  return {
    name: key,
    content,
    label,
    original: original || (content as string),
  };
};

export const getProductDetailsFeedbackItems = (
  paper: PaperDetails,
  extractedAnswer: string
): FeedbackItemContent[] => {
  const items: FeedbackItemContent[] = [];
  if (paper.abstract_takeaway) {
    items.push(
      generateFeedbackItemPayload(
        "paperDetails",
        "keyTakeaway",
        paper.abstract_takeaway,
        "Key takeaway"
      )
    );
  }
  if (extractedAnswer) {
    items.push(
      generateFeedbackItemPayload(
        "paperDetails",
        "answerFromSearch",
        extractedAnswer,
        "Answer from search"
      )
    );
  }
  if (paper.authors && paper.authors.length) {
    items.push(
      generateFeedbackItemPayload(
        "paperDetails",
        "authors",
        formatAuthors(paper.authors),
        "Authors",
        paper.authors.join(",")
      )
    );
  }

  const studyType = studyTypeBadges.find(
    (studyType) => studyType.value === paper.badges?.study_type
  );

  items.push(
    generateFeedbackItemPayload(
      "paperDetails",
      "studyType",
      studyType && studyType.label,
      "Study type",
      paper.badges?.study_type
    )
  );

  return items;
};

export const getStudySnapshotFeedbackItems = (
  paper: PaperDetails,
  fields: StudySnapshotFields | null,
  studyDetails: StudyDetailsResponse | null
) => {
  const snapshotFields =
    fields || parseStudyDetailsResponse(studyDetails, paper.badges);
  return Object.keys(snapshotFields).reduce((accumulator, key) => {
    const content = snapshotFields[key as keyof StudySnapshotFields] || "n/a";
    const derivedKey = key === "country" ? "location" : key;
    accumulator.push(
      generateFeedbackItemPayload(
        "studySnapshot",
        derivedKey,
        content,
        camelCaseToLabel(derivedKey)
      )
    );
    return accumulator;
  }, [] as FeedbackItemContent[]);
};

export const getJournalDetailsFeedbackItems = (
  paper: PaperDetails
): FeedbackItemContent[] => {
  const items: FeedbackItemContent[] = [];
  if (paper.journal.title) {
    items.push(
      generateFeedbackItemPayload(
        "journalDetails",
        "journalName",
        paper.journal.title,
        "Name"
      )
    );
  }

  items.push(
    generateFeedbackItemPayload(
      "journalDetails",
      "sjrScore",
      null,
      "SJR score",
      paper.journal.scimago_quartile ?? UNKNOWN_QUARTILE_VALUE
    )
  );
  return items;
};

export const extractFeedbackDataFromState = (
  state: StateType,
  query: string
) => {
  return Object.keys(state).reduce((accumulator, key) => {
    const value = (state as StateType)[key];
    if (!value.pristine) {
      const keys = key.split(":");
      const category = keys[0] as keyof FeedbackStateData;
      const name = keys[1] as FeedbackStateKeyType;
      const { pristine, ...theRest } = value;
      if (!accumulator[category]) {
        accumulator[category] = {
          [name]: name === "answerFromSearch" ? { ...theRest, query } : theRest,
        };
      } else {
        (accumulator[category] as any)[name] =
          name === "answerFromSearch" ? { ...theRest, query } : theRest;
      }
    }
    return accumulator;
  }, {} as FeedbackStateData);
};
